import type React from 'react';

import type { ABTestStatus } from '@/components/analytics/ABTestItem/ABTestItem';

/** Metric column identifiers used across all analytics data widgets */
export type MetricKey = 'views' | 'uv' | 'cv' | 'cvr';

/** Time range preset values for the analytics sidebar */
export type TimeRange = '7 Days' | '30 Days' | '90 Days';

/** Each metric key */
export const METRIC_LABELS: Record<MetricKey, string> = {
  views: 'Views',
  uv: 'UV',
  cv: 'CV',
  cvr: 'CVR',
};

/** Ordered list of metric keys (determines tab order in widgets) */
export const METRIC_KEYS: MetricKey[] = ['views', 'uv', 'cv', 'cvr'];

/** Active filter chip in the sidebar */
export interface FilterItem {
  /** Unique key for this filter (e.g. "sources:facebook.com") */
  id: string;
  /** Display label */
  label: string;
  /** Optional icon element (social icon, flag, UTM icon, etc.) */
  icon?: React.ReactNode;
}

/** Sidebar toggle states for traffic-related chart lines */
export interface TrafficToggles {
  views: boolean;
  uniqueVisitors: boolean;
  conversions: boolean;
  publishTimestamps: boolean;
}

/** Sidebar toggle states for metric cards visibility */
export interface MetricToggles {
  conversionRate: boolean;
  bounceRate: boolean;
}

/** Sidebar toggle states for conversion types */
export interface ConversionToggles {
  buttonClicks: boolean;
  formSubmissions: boolean;
  linkClicks: boolean;
}

export type { ABTestStatus };

/** A/B test item displayed in the sidebar */
export interface ABTestItem {
  id: string;
  name: string;
  status: ABTestStatus;
}

/** Traffic source row data used by analytics tables and stories */
export interface SourceData {
  domain: string;
  views: string | number;
  uv: string | number;
  cv: string | number;
  cvr: string | number;
}

/** Geographic row data used by analytics tables and stories */
export interface GeoData {
  country: string;
  countryCode?: string;
  flagUrl?: string;
  views: string | number;
  uv: string | number;
  cv: string | number;
  cvr: string | number;
}

/** Engagement metric row data used by widgets and stories */
export interface EngagementMetric {
  label: string;
  value: string | number;
  /** Trend change string like "+2.5%" or "-4%" */
  change?: string;
  /** Trend direction: positive (green) or negative (coral) */
  changeTrend?: 'positive' | 'negative';
}
